import { IGetRowsParams } from 'ag-grid-community'
import { format } from 'date-fns'

/**
 * Gets the custom column definition if it exists
 * @param colDef The column default definition
 * @returns the mapped value if it exists, otherwise returns the original value
 */
function getCustomColDef(colDef) {
  const customColDefMap = new Map([
    ['title_ordered', 'title_ordered'],
    ['title_received', 'title_report__received'],
    ['appraisal_ordered', 'appraisal_ordered'],
    ['appraisal_received', 'appraisal_received'],
    ['appraisal_review_status', 'appraisal_review_status'],
    ['appraised_home_value', 'valuations__value'],
    ['property_usage', 'property_usage'],
    ['property_type', 'property_type'],
    ['county', 'county'],
    ['estimated_value_with_overrides', 'estimated_value_with_overrides'],
    ['appraised_home_value', 'appraised_home_value'],
    ['report_ordered_at', 'report_ordered_at'],
    ['report_expires_at', 'report_expires_at'],
    ['calc_application_fico', 'calc_application_fico'],
    ['is_mtg_inquires', 'is_mtg_inquires'],
    ['starting_home_value', 'starting_home_value'],
    ['roof_solar_panel', 'roof_solar_panel'],
    ['investment_payment', 'investment_payment'],
    ['investment_percentage', 'investment_percentage'],
    ['exchange_rate', 'exchange_rate'],
    ['unlock_percentage', 'unlock_percentage'],
    ['annualized_cost_limit', 'annualized_cost_limit'],
    ['home_finance_limit', 'home_finance_limit'],
    ['fees_expenses_and_credits', 'fees_expenses_and_credits'],
    ['total_investment_cost_before_closing', 'total_investment_cost_before_closing'],
    ['total_payoffs', 'total_payoffs'],
    ['final_net_closing_proceeds', 'final_net_closing_proceeds'],
    ['exchange_rate_adjustment', 'exchange_rate_adjustment'],
    ['adjusted_exchange_rate', 'adjusted_exchange_rate'],
    ['fico_override', 'fico_override'],
    ['home_value_override', 'home_value_override'],
    ['annualized_cost_limit_override', 'annualized_cost_limit_override'],
    ['dscr_override', 'dscr_override'],
    ['min_investment_override', 'min_investment_override'],
    ['max_investment_override', 'max_investment_override'],
    ['annualized_cost_limit_calc_version', 'annualized_cost_limit_calc_version'],
    ['subject_property_address', 'subject_property_address'],
    ['unit_number', 'unit_number'],
    ['assessor_parcel_number', 'assessor_parcel_number'],
    ['monthly_gross_rent', 'monthly_gross_rent'],
    ['aquired_price', 'aquired_price'],
    ['title_vesting', 'title_vesting'],
    ['title_held', 'title_held'],
    ['monthly_cost_principle_interest', 'monthly_cost_principle_interest'],
    ['monthly_cost_taxes', 'monthly_cost_taxes'],
    ['monthly_cost_insurance_hazard', 'monthly_cost_insurance_hazard'],
    ['monthly_cost_hoa', 'monthly_cost_hoa'],
    ['first_name', 'first_name'],
    ['last_name', 'last_name'],
    ['email', 'email'],
    ['phone', 'phone'],
    ['born_on', 'born_on'],
    ['marital_status', 'marital_status'],
    ['social_security_number', 'social_security_number'],
    ['mailing_address', 'mailing_address'],
    ['state', 'state'],
    ['zip_code', 'zip_code'],
    ['automated_review_status', 'automated_review_status'],
    ['avm_variance', 'avm_variance'],
    ['settlement_agent', 'settlement_agent'],
    ['file_number', 'file_number'],
    ['title_report_effective_date', 'title_report_effective_date'],
    ['title_report_expiration_date', 'title_report_expiration_date'],
    ['appraisal_effective_date', 'appraisal_effective_date'],
    ['legal_description', 'legal_description'],
    ['taxes', 'taxes'],
    ['noo_name_of_spouse', 'noo_name_of_spouse'],
    ['appraisal_type', 'appraisal_type'],
    ['property_condition', 'property_condition'],
    ['appraisal_expiration_date', 'appraisal_expiration_date'],
    ['name', 'name'],
    ['city', 'city'],
    ['delay_reasons', 'delay_reasons'],
    ['ops_delay_reasons', 'ops_delay_reasons'],
    ['application_state_modified_at', 'application_state_modified_at'],
    ['estimated_close_on', 'estimated_close_on'],
    ['modified_at', 'modified_at'],
    ['external_id', 'external_id'],
    ['requested_investment', 'requested_investment'],
    ['title_received', 'title_received'],
    ['uw_submitted_at', 'uw_submitted_at'],
    ['hubspot_contact_id', 'hubspot_contact_id'],
    ['loss_type', 'loss_type'],
    ['loss_reason', 'loss_reason'],
    ['title_pr_status', 'title_pr_status'],
    ['approved_escalations', 'approved_escalations'],
    ['application_party_name', 'application_party_name'],
    ['agreement_effective_on', 'agreement_effective_on'],
    ['call_completed', 'call_completed'],
    ['follow_up', 'follow_up'],
    ['call_scheduled', 'call_scheduled'],
    ['val_express', 'val_express'],
  ])

  if (customColDefMap.has(colDef)) {
    return customColDefMap.get(colDef)
  }
  return colDef
}

export function parseFiltersAndSorting(prevParams, params: IGetRowsParams, columnDefs) {
  return {
    ...prevParams,
    ...parseFilters(params.filterModel),
    order_by: parseSort(params.sortModel, columnDefs),
  }
}

function parseFilters(filterModel) {
  const filters = {}

  if ('cltv' in filterModel) {
    filterModel['cltv']['filter'] /= 100
  }

  for (const colDef in filterModel) {
    const { filter, filterTo, filterType, type, dateFrom, dateTo, values } = filterModel[colDef]

    // Custom parsing for deal_teams
    if (colDef === 'deal_teams') {
      if (['blank', 'notBlank'].includes(type)) {
        filters['deal_teams__isnull'] = type === 'blank'
        continue
      }
      if (type === 'in') {
        filters['deal_teams_user_ids'] = filter
        continue
      }
    }

    if (['notBlank', 'blank'].includes(type)) {
      filters[`${getCustomColDef(colDef)}__isnull`] = type === 'blank'
      continue
    }

    if (filterType === 'text') {
      if (type === 'notEqual') {
        filters[`${getCustomColDef(colDef)}__iregex`] = `^(?!${filter}$).+$`
        continue
      } else if (type === 'notContains') {
        filters[`${getCustomColDef(colDef)}__iregex`] = `^(?!.*${filter})(.+)$`
        continue
      } else {
        const lookup = {
          equals: '',
          contains: '__icontains',
          startsWith: '__istartswith',
          endsWith: '__iendswith',
          in: '__in',
        }
        filters[`${getCustomColDef(colDef)}${lookup[type]}`] = filter
      }
    }

    if (filterType === 'date') {
      if (type === 'notEqual') {
        filters[`${getCustomColDef(colDef)}__ne`] = format(new Date(dateFrom), 'yyyy-MM-dd')
      } else if (type === 'equals') {
        filters[`${getCustomColDef(colDef)}__date`] = format(new Date(dateFrom), 'yyyy-MM-dd')
        continue
      } else if (type !== 'inRange') {
        const lookup = {
          greaterThan: '__gt',
          lessThan: '__lt',
        }
        filters[`${getCustomColDef(colDef)}${lookup[type]}`] = format(
          new Date(dateFrom),
          'yyyy-MM-dd'
        )
        continue
      } else {
        filters[`${getCustomColDef(colDef)}__range`] = [
          format(new Date(dateFrom), 'yyyy-MM-dd'),
          format(new Date(dateTo), 'yyyy-MM-dd'),
        ]
      }
    }

    if (filterType === 'number') {
      if (type === 'notEqual') {
        if (
          [
            'estimated_value_with_overrides',
            'appraised_home_value',
            'exchange_rate_adjustment',
            'home_value_override',
            'annualized_cost_limit_override',
            'dscr_override',
            'min_investment_override',
            'max_investment_override',
            'requested_investment',
            'monthly_gross_rent',
            'monthly_cost_principle_interest',
            'monthly_cost_taxes',
            'monthly_cost_insurance_hazard',
            'monthly_cost_hoa',
          ].includes(colDef)
        ) {
          filters[`${getCustomColDef(colDef)}__not_equal`] = filter
          continue
        } else {
          filters[`${getCustomColDef(colDef)}__iregex`] = `^(?!${filter}$).+$`
          continue
        }
      } else if (type !== 'inRange') {
        const lookup = {
          equals: '',
          greaterThan: '__gt',
          lessThan: '__lt',
          greaterThanOrEqual: '__gte',
          lessThanOrEqual: '__lte',
          contains: '__icontains',
          startsWith: '__istartswith',
          endsWith: '__iendswith',
        }
        filters[`${getCustomColDef(colDef)}${lookup[type]}`] = filter
        continue
      } else {
        filters[`${getCustomColDef(colDef)}__range`] = [filter, filterTo]
      }
    }

    // Handle picklist (set) filter type
    if (filterType === 'set') {
      if (values?.length === 0) {
        filters[`${getCustomColDef(colDef)}__in`] = 'None'
      } else {
        filters[`${getCustomColDef(colDef)}__in`] = values
      }
    }
  }

  // delete empty filters
  for (const filter in filters) {
    if (filters[filter] === '') {
      delete filters[filter]
    }
  }

  return filters
}

function parseSort(sortModel, columnDefs) {
  if (sortModel?.length > 0) {
    const { colId, sort } = sortModel[0]

    // When sorting by application_state, tell the API to use application_state_index
    if (colId === 'application_state') {
      return `${sort === 'asc' ? '' : '-'}application_state_index`
    }

    /*
     * Django order_by syntax:
     * column_name is ascending
     * -column_name is descending
     * if its a nested property
     * relation___column_name
     * -relation___column_name
     */
    const column = columnDefs?.find((column) => column.field === colId)
    const lookup = column?.source.includes('.') ? column?.source.replaceAll('.', '__') : colId
    return `${sort === 'asc' ? '' : '-'}${lookup}`
  }
  return '-modified_at'
}
